import { isEmpty } from "lodash";
import qs from "qs";
import { z } from "zod";

import {
  OpportunityWorkflow_Transitions,
  PaginationOptions,
  SearchOpportunitiesRequest,
} from "lib/generated/app-service-gql/graphql";
import { parseBooleanValue, parseNumberValue } from "lib/hooks/useURLState";
import { routes } from "lib/routes";
import {
  OpportunityEntityType,
  OpportunitySortBy,
  OpportunitySortOrder,
  OpportunityWorkflowTransitionGroup,
} from "lib/types/graphQLEnums";
import { ColumnSetting, CountryRegion } from "lib/types/models";
import { capitalize } from "lib/utils";
import { OpportunityDetails } from "../../lib/hooks/api/opportunities/useOpportunity";
import { OpportunityColumns } from "./opportunities_table/OpportunitiesTable";

type DateFilter = {
  filter?: { from?: string; relativeFrom?: string; to?: string; relativeTo?: string };
  hideNulls?: boolean;
};
export type ValueFilter = { from?: number; to?: number; hideNulls?: boolean };

export type OpportunityFilters = {
  searchText?: string;
  assignee?: { ids: string[]; includeNulls?: boolean };
  stageIds?: string[];
  closeDate?: DateFilter;
  lastUpdatedDate?: DateFilter;
  value?: ValueFilter;
  buyers?: string[];
  buyerLists?: string[];
  buyerCategories?: string[];
  buyerCountryRegions?: CountryRegion;
  order?: OpportunitySortOrder;
  orderBy?: OpportunitySortBy;
  supplierGuids?: string[];
  supplierSme?: string;
  teamIds?: string[];
};

// Define empty values explicitly as otherwise nested filter form fields fail to be cleared properly
export const EMPTY_OPPORTUNITY_FILTERS: OpportunityFilters = {
  searchText: "",
  assignee: { ids: [], includeNulls: undefined },
  stageIds: [],
  closeDate: { filter: undefined, hideNulls: undefined },
  lastUpdatedDate: { filter: undefined, hideNulls: undefined },
  value: { from: undefined, to: undefined, hideNulls: undefined },
  buyers: [],
  buyerLists: [],
  buyerCategories: [],
  buyerCountryRegions: { countries: [], regions: [], selected: [] },
  orderBy: OpportunitySortBy.UpdatedAt,
  order: OpportunitySortOrder.Desc,
  supplierGuids: [],
  supplierSme: undefined,
  teamIds: [],
};

export const DEFAULT_PAGINATION: { pageSize: number; page: number } = {
  pageSize: 10,
  page: 1,
};

export const DEFAULT_WORKFLOW_TRANSITION_GROUPS = [
  OpportunityWorkflowTransitionGroup.Bidding,
  OpportunityWorkflowTransitionGroup.PreEngagement,
  OpportunityWorkflowTransitionGroup.TenderQualification,
];

export function getDefaultOpportunityStageIds(transitions: OpportunityWorkflow_Transitions[]) {
  return (
    transitions
      .filter((transition) => DEFAULT_WORKFLOW_TRANSITION_GROUPS.includes(transition.group))
      .map((transition) => transition.stage.id) ?? []
  );
}

export type SortField = OpportunitySortBy;

export function convertOpportunityFiltersToSearchRequest(
  filters: OpportunityFilters,
): SearchOpportunitiesRequest {
  let assignee: SearchOpportunitiesRequest["assignee"];
  if (filters.assignee && (filters.assignee.ids.length > 0 || filters.assignee.includeNulls)) {
    assignee = filters.assignee;
  }

  let buyerFilters: SearchOpportunitiesRequest["buyerFilters"];
  if (
    filters.buyers?.length ||
    filters.buyerLists?.length ||
    filters.buyerCategories?.length ||
    filters.buyerCountryRegions?.countries?.length ||
    filters.buyerCountryRegions?.regions?.length
  ) {
    buyerFilters = {
      buyers: filters.buyers,
      buyerLists: filters.buyerLists,
      buyerCategories: filters.buyerCategories,
      buyerCountryCodes: filters.buyerCountryRegions?.countries,
      buyerRegions: filters.buyerCountryRegions?.regions,
    };
  }

  let supplierFilters: SearchOpportunitiesRequest["supplierFilters"];
  if (filters.supplierGuids?.length || filters.supplierSme) {
    supplierFilters = {
      ids: filters.supplierGuids?.filter(
        (id) => !["SIGNALS:ALL_COMPETITORS", "SIGNALS:ALL_PARTNERS"].includes(id),
      ),
      isSME: filters.supplierSme ? filters.supplierSme.toLowerCase() === "true" : undefined,
    };
  }

  return {
    searchText: filters.searchText,
    assignee: assignee,
    stageIds: filters.stageIds,
    closeDate: isEmpty(filters.closeDate) ? undefined : filters.closeDate,
    lastUpdatedDate: isEmpty(filters.lastUpdatedDate) ? undefined : filters.lastUpdatedDate,
    value: isEmpty(filters.value) ? undefined : filters.value,
    buyerFilters,
    supplierFilters,
    teamIds: filters.teamIds,
    order: filters.order,
    orderBy: filters.orderBy,
  };
}

export function parseOpportunitiesUrlState(
  state: unknown,
  defaultFilters = {},
): OpportunityFilters {
  if (typeof state !== "object" || state === null) {
    return defaultFilters;
  }

  const filters = state as OpportunityFilters;

  return {
    ...defaultFilters,
    ...filters,
    assignee: {
      ids: filters.assignee?.ids ?? [],
      includeNulls: parseBooleanValue(filters.assignee?.includeNulls),
    },
    closeDate: {
      ...filters.closeDate,
      hideNulls: parseBooleanValue(filters.closeDate?.hideNulls),
    },
    lastUpdatedDate: {
      ...filters.lastUpdatedDate,
      hideNulls: parseBooleanValue(filters.lastUpdatedDate?.hideNulls),
    },
    value: {
      from: parseNumberValue(filters.value?.from),
      to: parseNumberValue(filters.value?.to),
      hideNulls: parseBooleanValue(filters.value?.hideNulls),
    },
  };
}

export function parsePaginationUrlState(state: unknown): PaginationOptions {
  if (!state || typeof state !== "object") {
    return DEFAULT_PAGINATION;
  }

  let parsedState = state as PaginationOptions;
  return {
    pageSize: parseNumberValue(parsedState.pageSize) ?? DEFAULT_PAGINATION.pageSize,
    page: parseNumberValue(parsedState.page) ?? DEFAULT_PAGINATION.page,
  };
}

export function generateOpportunitiesSearchUrl(filters: OpportunityFilters): string {
  const params = qs.stringify(
    { opportunityFilters: filters },
    { arrayFormat: "brackets", addQueryPrefix: true },
  );
  return `${routes.opportunities}${params}`;
}

const keysToKeep: (keyof OpportunityDetails)[] = [
  "id",
  "name",
  "description",
  "value",
  "closeDate",
  "routeToMarket",
];

export const filterObject = (obj: Record<string, unknown>, keys: string[]) => {
  return Object.keys(obj)
    .filter((key) => keys.includes(key))
    .reduce<Record<string, unknown>>((acc, key) => {
      acc[key] = obj[key];
      return acc;
    }, {});
};

export const filterOpportunityDefaultValues = (opportunity: OpportunityDetails) => {
  const filteredPayload = filterObject(opportunity, keysToKeep);
  return {
    ...filteredPayload,
    assignedToId: opportunity.assignedTo?.guid ?? null,
    stageId: opportunity.stage.id,
  };
};

export const getOpportunityStageById = (
  id: string,
  transitions: OpportunityWorkflow_Transitions[],
) => {
  return transitions.find((transition) => transition.stage.id === id);
};

export const DisplayableEntity = {
  Notice: "notice",
  Document: "document",
  Framework: "framework",
  Supplier: "supplier",
  Buyer: "buyer",
  Contact: "contact",
};

export type DisplayableEntities =
  | "notice"
  | "document"
  | "framework"
  | "supplier"
  | "buyer"
  | "contact";

export const getEntityLabel = (entity: DisplayableEntities, capitalise = true) => {
  const pluralEntity = `${entity}s`;

  switch (entity) {
    case "notice":
    case "framework":
    case "buyer":
    case "contact":
    case "supplier":
      return capitalise ? capitalize(pluralEntity) : pluralEntity;
    case "document":
      return `${capitalise ? "Strategic" : "strategic"} & planning documents`;
  }
};

export const ALL_AVAILABLE_OPPORTUNITY_COLUMNS: ColumnSetting<OpportunityColumns>[] = [
  { title: "Opportunity name", field: "name", disabled: true },
  { title: "Buyer", field: "buyers" },
  { title: "Stage", field: "stage" },
  { title: "Assigned to", field: "assignedTo" },
  { title: "Value", field: "value" },
  { title: "Created", field: "createdAt" },
  { title: "Last updated", field: "updatedAt" },
  { title: "Close date", field: "closeDate" },
  { title: "Relevant suppliers", field: "suppliers" },
];

const columnCopies: Record<OpportunityColumns, string> = ALL_AVAILABLE_OPPORTUNITY_COLUMNS.reduce(
  (acc, { title, field }) => {
    if (field === "buyers") {
      return { ...acc, buyerName: title };
    }
    if (field === "stage") {
      return { ...acc, stageName: title };
    }
    return { ...acc, [field]: title };
  },
  {} as Record<OpportunityColumns, string>,
);

export const getOpportunitiesColumnCopy = (column?: string): string => {
  if (!column || !(column in columnCopies)) return "";
  return columnCopies[column as OpportunityColumns];
};

export const opportunitySchema = z.object({
  name: z.string().min(1, "Name is required"), // Ensures name is required
  value: z.coerce.number().nonnegative().optional(),
  stageId: z.string().uuid({ message: "Stage is required" }), // Ensures stage is required
  assignedToId: z.string().uuid().optional(),
  // transform the date object to a timestamp string
  closeDate: z.coerce
    .date()
    .transform((date) => date.toISOString())
    .optional(),
  submissionDate: z.coerce
    .date()
    .transform((date) => date.toISOString())
    .optional(),
  existingExpiryDate: z.coerce
    .date()
    .transform((date) => date.toISOString())
    .optional(),
  entities: z
    .object({
      entityId: z.string().uuid(),
      entityType: z.nativeEnum(OpportunityEntityType),
    })
    .array()
    .optional(),
});
